/**
 * ThemeContext for UI-Model-1 (API-Direct Approach)
 *
 * Thin wrapper context that:
 * - Reads theme data from UIModelStateContext (state.model.theme)
 * - Dispatches updates via UIModelStateContext
 * - Provides domain-specific convenience methods
 *
 * Line limit: ~80 lines
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { DEFAULT_THEME_CONFIGURATION } from '../types/state.types';
import { ThemeContextValue } from '../types/context.types';
import { useUIModelState } from './UIModelStateContext';

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { state, dispatch } = useUIModelState();

  // ============================================================================
  // Derived State (from UIModelState)
  // ============================================================================

  const theme = useMemo<Record<string, unknown> | null>(() => {
    if (!state.model?.theme) return null;
    return state.model.theme;
  }, [state.model?.theme]);

  // ============================================================================
  // Update Operations
  // ============================================================================

  const updateTheme = useCallback((updates: Record<string, unknown>) => {
    if (!state.model) return;

    const currentTheme = state.model.theme || DEFAULT_THEME_CONFIGURATION;

    const newModel = {
      ...state.model,
      theme: {
        ...currentTheme,
        ...updates,
      },
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'theme' } });
  }, [state.model, dispatch]);

  const resetTheme = useCallback(() => {
    if (!state.model) return;

    const newModel = {
      ...state.model,
      theme: DEFAULT_THEME_CONFIGURATION,
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'theme' } });
  }, [state.model, dispatch]);

  const setThemeProperty = useCallback((path: string, value: unknown) => {
    if (!state.model) return;

    const currentTheme = state.model.theme || DEFAULT_THEME_CONFIGURATION;
    const keys = path.split('.');
    const updated = { ...currentTheme };
    let current: Record<string, unknown> = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      current[key] = { ...(current[key] as Record<string, unknown>) };
      current = current[key] as Record<string, unknown>;
    }

    current[keys[keys.length - 1]] = value;

    const newModel = {
      ...state.model,
      theme: updated,
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'theme' } });
  }, [state.model, dispatch]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      updateTheme,
      resetTheme,
      setThemeProperty,
    }),
    [theme, updateTheme, resetTheme, setThemeProperty]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
